import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Container,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

// Custom styles for dropdown
const dropdownMenuProps = {
  PaperProps: {
    style: {
      maxHeight: '300px'
    },
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

// Style for MenuItem to make them compact
const compactMenuItemStyle = {
  fontSize: '0.875rem',
  paddingTop: '6px',
  paddingBottom: '6px',
  minHeight: '32px',
};

const CompanyPartnerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [foreignAgents, setForeignAgents] = useState([]);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  const [formData, setFormData] = useState({
    companyID: '',
    foreignAgent: '',
    isActive: true,
  });

  const isEdit = !!id;

  useEffect(() => {
    fetchCompanies();
    fetchForeignAgents();
  }, []);

  useEffect(() => {
    if (isEdit) {
      fetchPartner();
    } else if (location.state?.companyId) {
      setFormData(prev => ({
        ...prev,
        companyID: location.state.companyId
      }));
    }
  }, [id, location.state]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/Companies/active-agencies');
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to load companies. Please try again.');
    }
  };

  const fetchForeignAgents = async () => {
    try {
      const response = await api.get('/Companies/all_agents');
      setForeignAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching foreign agents:', error);
      setError('Failed to load foreign agents. Please try again.');
    }
  };

  const fetchPartner = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/CompanyPartners/${id}`);
      const data = response.data;
      setFormData({
        companyID: data.companyID || '',
        foreignAgent: data.foreignAgentID || '',
        isActive: data.isActive !== undefined ? data.isActive : true,
      });
    } catch (error) {
      console.error('Error fetching partner:', error);
      setError('Failed to load partner data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const requiredFields = {
    companyID: 'Company is required',
    foreignAgent: 'Foreign Agent is required',
  };

  const validateField = (field, value) => {
    if (requiredFields[field] && !value) {
      return requiredFields[field];
    }
    return null;
  };

  const validateForm = () => {
    const errors = {};
    const touched = {};
    let hasErrors = false;

    Object.keys(requiredFields).forEach(field => {
      touched[field] = true;
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    setTouchedFields(touched);
    return !hasErrors;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleBlur = (field) => (event) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
    
    const error = validateField(field, formData[field]);
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSubmit = async () => {
    const isValid = validateForm();
    
    if (!isValid) {
      const firstErrorField = Object.keys(validationErrors)[0];
      if (firstErrorField) {
        const element = document.getElementById(`field-${firstErrorField}`);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }, 300);
        }
      }
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const submitData = {
        companyID: formData.companyID,
        foreignAgent: formData.foreignAgent,
        isActive: formData.isActive,
      };

      if (isEdit) {
        submitData.partnerID = id;
        await api.put(`/CompanyPartners/${id}`, submitData);
      } else {
        await api.post('/CompanyPartners', submitData);
      }

      navigate('/companies/companypartners');
    } catch (error) {
      console.error('Error saving partner:', error);
      if (error.response) {
        if (error.response.status === 409) {
          setError('This partner relationship already exists.');
        } else {
          setError(`Failed to save partner: ${error.response.data || error.response.statusText}`);
        }
      } else {
        setError('Failed to save partner. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/companies/companypartners');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" fontSize="1.5rem">
            {isEdit ? 'Edit Partner' : 'Create New Partner'}
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={isSaving}
              sx={{ mr: 1 }}
              size="small"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              size="small"
            >
              Cancel
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <Card sx={{ bgcolor: '#fafafa' }}>
          <CardContent>
            <Grid container spacing={2} direction="column">
              {/* Company - Full width, vertical */}
              <Grid item xs={12}>
                <FormControl 
                  fullWidth 
                  required
                  error={!!validationErrors.companyID && touchedFields.companyID}
                  size="small"
                >
                  <InputLabel>Company</InputLabel>
                  <Select
                    id="field-companyID"
                    value={formData.companyID || ''}
                    onChange={handleChange('companyID')}
                    onBlur={handleBlur('companyID')}
                    label="Company"
                    // Removed disabled={isEdit} - Company can be changed on edit
                    MenuProps={dropdownMenuProps}
                    size="small"
                  >
                    <MenuItem value="" sx={compactMenuItemStyle}>
                      <em>Select a company</em>
                    </MenuItem>
                    {companies.map((company) => (
                      <MenuItem key={company.companyID} value={company.companyID} sx={compactMenuItemStyle}>
                        {company.companyName} - {company.country || 'N/A'}
                      </MenuItem>
                    ))}
                  </Select>
                  {touchedFields.companyID && validationErrors.companyID && (
                    <Typography variant="caption" color="error">
                      {validationErrors.companyID}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Foreign Agent - Full width, vertical */}
              <Grid item xs={12}>
                <FormControl 
                  fullWidth 
                  required
                  error={!!validationErrors.foreignAgent && touchedFields.foreignAgent}
                  size="small"
                >
                  <InputLabel>Foreign Agent</InputLabel>
                  <Select
                    id="field-foreignAgent"
                    value={formData.foreignAgent || ''}
                    onChange={handleChange('foreignAgent')}
                    onBlur={handleBlur('foreignAgent')}
                    label="Foreign Agent"
                    MenuProps={dropdownMenuProps}
                    size="small"
                  >
                    <MenuItem value="" sx={compactMenuItemStyle}>
                      <em>Select a foreign agent</em>
                    </MenuItem>
                    {foreignAgents.map((agent) => (
                      <MenuItem key={agent.companyID} value={agent.companyID} sx={compactMenuItemStyle}>
                        {agent.companyName} - {agent.country || 'N/A'}
                      </MenuItem>
                    ))}
                  </Select>
                  {touchedFields.foreignAgent && validationErrors.foreignAgent && (
                    <Typography variant="caption" color="error">
                      {validationErrors.foreignAgent}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Is Active Switch - Full width */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleChange('isActive')}
                      color="primary"
                      size="small"
                    />
                  }
                  label={formData.isActive ? 'Active' : 'Inactive'}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Info message */}
        <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary" fontSize="0.8rem">
            <strong>Note:</strong> A partner relationship links a company with a foreign agent. 
            Each company can have multiple foreign agents, but each combination of company and 
            foreign agent must be unique.
          </Typography>
        </Box>

        {/* Action Buttons at bottom */}
        <Box display="flex" justifyContent="center" gap={2} mt={3} pt={2} borderTop={1} borderColor="divider">
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : isEdit ? 'Update Partner' : 'Create Partner'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="medium"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default CompanyPartnerForm;